// Start Encounter, shared by the strip, the tracker (issue 1021) and Combat › Start encounter (issue 1425). Starting
// sorts by initiative first: in the 2026-10-04 playtest the goblin rolled
// higher, the first combatant in the list went first anyway, and a Sort after
// Start kept the turn where it was, so the round's end fell before the
// goblin. Anyone who has not rolled is named first, with the choice to roll
// for them or start anyway (an unrolled combatant sorts last).
//
// The flow is the EXISTING events in order — encounter.roll (when asked),
// encounter.sort, encounter.start — never a new meaning for encounter.start:
// the log replays every past fight, and a start that began sorting would
// replay those fights in an order nobody saw. Each step waits for the reply,
// and a reply means the event is committed (accepted means committed), so
// the sort reads the rolls and the start reads the sort.
import { useEffect, useRef, useState } from 'react';
import type { Encounter, TableMember, TableObject } from '@dragons-table/vtt-shared';
import { useTableState } from '../../table/TableState';
import { startNotes } from './InitiativeStrip';
import { BTN } from '../ui';

// The tracker's Init boxes as typed (issue 1425's follow-up). A box saves when it is left, and the save lands a
// moment later: a DM who cleared one and pressed Start straight away was started with no question, because the page
// still held the old roll. So each row registers what its box reads, and a start counts that text, not the store.
const initDrafts = new Map<string, () => string>();

/** A tracker row's Init box text, read by the start's has-not-rolled check while the row is mounted. */
export function useInitDraft(id: string, text: string): void {
  const ref = useRef(text);
  ref.current = text;
  useEffect(() => {
    const read = () => ref.current;
    initDrafts.set(id, read);
    return () => { if (initDrafts.get(id) === read) initDrafts.delete(id); };
  }, [id]);
}

/** A combatant's initiative as its Init box reads now: empty is no roll; text that is not a number keeps the stored one (the save ignores it too). */
function rollOf(c: Encounter['combatants'][number]): number | null {
  const read = initDrafts.get(c.id);
  if (!read) return c.initRoll;
  const t = read().trim();
  if (t === '') return null;
  const r = Math.trunc(Number(t));
  return Number.isFinite(r) ? r : c.initRoll;
}

/** The combatants with no initiative yet, in list order — an Init box being edited counts as it reads. */
export function unrolled(enc: Encounter): Encounter['combatants'] {
  return enc.combatants.filter((c) => typeof rollOf(c) !== 'number');
}

/** A tracker box still focused (iPad Safari leaves it focused when a button is tapped) is left first, so its save is
 *  sent before the sort and the start: the socket keeps the order, and the server sorts what the box said. */
function leaveTrackerBox(): void {
  const el = typeof document === 'undefined' ? null : document.activeElement;
  if (el instanceof HTMLInputElement && el.closest('[data-testid^="combatant-"]')) el.blur();
}

const btn = BTN;

export function useStartEncounter(enc: Encounter, objects: Record<string, TableObject>, members: TableMember[]) {
  const { act, setNotice, systemRules } = useTableState();
  const [asking, setAsking] = useState<string[] | null>(null);

  // `said`: what the roll before it told the DM (qa-runs 1194: who could not be thrown), kept beside the start's own notice.
  const begin = (said?: string) => {
    const start = () => act({ kind: 'encounter.start' }, (reply) => {
      if (!reply.ok) return;
      const words = [said, ...startNotes(enc, objects, members)].filter(Boolean).join(' ');
      if (words) setNotice(words);
    });
    // A system with no initiative rules (issue 1303) is ordered by hand: the fight starts in the order the DM left.
    if (!systemRules.initiative) { start(); return; }
    act({ kind: 'encounter.sort' }, (sorted) => { if (sorted.ok) start(); });
  };

  const request = () => {
    leaveTrackerBox();
    // With no initiative rules nothing is rolled (issue 1305): the unrolled are not asked about.
    const names = systemRules.initiative ? unrolled(enc).map((c) => c.name) : [];
    if (names.length) { setAsking(names); return; }
    begin();
  };

  const rollThenBegin = () => {
    const ids = unrolled(enc).map((c) => c.id);
    setAsking(null);
    // Refused in words (issue 1525: a rated count with nobody's number of dice known): said, and nothing starts.
    act({ kind: 'encounter.roll', ids }, (reply) => { if (reply.ok) begin(reply.notice); else setNotice(reply.reason ?? 'Initiative could not be rolled.'); });
  };

  const prompt = asking ? (
    <div role="group" aria-label="Before the encounter starts" className="flex flex-wrap items-center gap-1 text-xs text-stone-300" data-testid="start-unrolled">
      <span>{asking.join(', ')} {asking.length === 1 ? 'has' : 'have'} not rolled initiative.</span>
      <button type="button" className={`${btn} border-amber-600`} onClick={rollThenBegin}>Roll for {asking.length === 1 ? 'them' : 'them all'} and start</button>
      <button type="button" className={btn} onClick={() => { setAsking(null); begin(); }}>Start anyway</button>
      <button type="button" className={btn} onClick={() => setAsking(null)}>Cancel</button>
    </div>
  ) : null;

  return { request, prompt, cancel: () => setAsking(null) };
}
